import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AccountBalanceOutlined, Add, MonetizationOnOutlined, Store, Timeline } from '@mui/icons-material';
import { isEmpty, orderBy } from 'lodash';
import queryString from 'query-string';
import { useNavigate } from 'react-router-dom';
import { Company } from 'api';
import { allocationsAction, captableAction } from 'common/actions';
import { baseCompanyUrl, companySummaryUrl, fundSummaryUrl, signInPath } from 'common/config/urls';
import { DOCUMENT_GENERATION_JOBS_SLUG } from 'common/constants/documents';
import { DOCUMENT_GENERATION_JOBS } from 'common/constants/features';
import { FUND_PERMISSION_NAME } from 'common/constants/sidebar';
import { PINNED_COMPANIES_KEY, PINNED_FUND_INVESTMENTS_KEY, PINNED_FUNDS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { NativeScroll } from 'components/NativeScroll';
import LayoutContext from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { useHandleClickDialog } from 'layouts/Main/components';
import {
  FUND_INVESTMENTS_COMPANIES_TITLE,
  FUND_INVESTMENTS_DIRECT_INVESTMENTS_TITLE,
  FUND_INVESTMENTS_NEW_COMPANY_TITLE,
  FUND_INVESTMENTS_NEW_DIRECT_INVESTMENT_TITLE,
  FUND_INVESTMENTS_NEW_FUND_INVESTMENT_SLUG,
  FUND_INVESTMENTS_NEW_FUND_INVESTMENT_TITLE,
  FUND_INVESTMENTS_SLUG,
  FUND_INVESTMENTS_TITLE,
} from 'pages/FundInvestments/common/constants';
import { FUND_INVESTMENTS_PAGES } from 'pages/FundInvestments/common/pages';
import { getBaseFundInvestmentsUrl, getFundInvestmentsSummaryUrl } from 'pages/FundInvestments/common/urls';
import { useKeyboardShortcuts } from 'services/hooks';
import { useDialogFormsStore } from 'store';
import { getArrayValue, getObjectValue } from 'utilities';
import { COMPANY_PAGES, PINNABLE_TYPES } from '../../constants';
import { Option } from '../CompanyMenuItem';
import DropDown from '../DropDown';
import FundFilter from '../FundFilter';
import ItemsFilter from '../ItemsFilter';
import ListItems from '../ListItems';
import { TopMenuItemsProps } from './TopMenuItems.props';
import { Root, TopContainer } from './TopMenuItems.styles';
import { MenuItem, SubMenuItem } from '../type';

const TopMenuItems = ({ open = false, renderFundsAndCompanies, setOpenSidebar }: TopMenuItemsProps) => {
  const navigate = useNavigate();
  const [, checkFeature] = useCheckFeatures();

  const { action } = useContext(UnsavedChangesContext);
  const { commonMeasurementDate, viewOnlyUser } = useContext(LayoutContext);

  const [storeValue, dispatch] = useStore();
  const { firmInfo, companyInfo, fundList, companyList, isRestrictedUser, otherPermissions, user, fundInfo } =
    getObjectValue(storeValue);

  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const [pinnedCompanies, setPinnedCompanies] = useLocalStorageByUser(PINNED_COMPANIES_KEY, []);
  const [pinnedFundInvestments, setPinnedFundInvestments] = useLocalStorageByUser(PINNED_FUND_INVESTMENTS_KEY, []);
  const [pinnedFunds, setPinnedFunds] = useLocalStorageByUser(PINNED_FUNDS_KEY, []);

  const updatePinStatus = useCallback(
    (
      id: number,
      title: string,
      isPinned: boolean,
      type: (typeof PINNABLE_TYPES)[keyof typeof PINNABLE_TYPES] = PINNABLE_TYPES.COMPANY
    ) => {
      let pinnedItems: any[];
      let setPinnedItems: (items: any[]) => void;

      switch (type) {
        case PINNABLE_TYPES.FUND_INVESTMENT:
          pinnedItems = pinnedFundInvestments;
          setPinnedItems = setPinnedFundInvestments;
          break;
        case PINNABLE_TYPES.FUND:
          pinnedItems = pinnedFunds;
          setPinnedItems = setPinnedFunds;
          break;
        default: // PINNABLE_TYPES.COMPANY
          pinnedItems = pinnedCompanies;
          setPinnedItems = setPinnedCompanies;
          break;
      }

      let tmpPinnedOptions: any = {};

      if (isPinned) {
        tmpPinnedOptions = pinnedItems.filter((o: any) => o.value !== id) ?? [];
      } else {
        tmpPinnedOptions = [...pinnedItems, { value: id, label: title }];
      }

      setPinnedItems(tmpPinnedOptions);
    },
    [pinnedCompanies, setPinnedCompanies, pinnedFundInvestments, setPinnedFundInvestments, pinnedFunds, setPinnedFunds]
  );

  // Dialog Forms Store
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const setDisplayFundInvestmentDialogForm = useDialogFormsStore(state => state.setDisplayFundInvestmentDialogForm);

  const [anchorEl, setAnchorEl] = useState<DOMRect | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [subMenuItem, setSubMenuItem] = useState<any>();

  const dropDownRef = useRef<HTMLElement>(null);

  const companyPages = useMemo(() => [...COMPANY_PAGES], []);
  const fundInvestmentsPages = useMemo(() => [...FUND_INVESTMENTS_PAGES], []);

  const generationJobTabDetails = useMemo(
    () => ({
      id: 8,
      title: 'Document Generation Jobs',
      slug: DOCUMENT_GENERATION_JOBS_SLUG,
    }),
    []
  );

  const handleClick = useHandleClickDialog();

  const getMeasurementDateSlug = (menuItem: any) =>
    menuItem.company_measurement_dates?.find((cmd: any) => cmd.date === commonMeasurementDate)?.slug ??
    menuItem.measurement_date_slug;

  const getCompanySubMenu = (menuItem: any) => {
    const { company_measurement_dates: companyMeasurementDates, company_type: companyType = '' } =
      getObjectValue(menuItem);

    const generationJobsEnabled = checkFeature(DOCUMENT_GENERATION_JOBS);
    let services = generationJobsEnabled ? [...companyPages, generationJobTabDetails] : companyPages;

    if (companyType === Company.type.INVESTMENT) {
      services = [...fundInvestmentsPages];
    }

    const companyHasMeasurementDate = commonMeasurementDate && companyMeasurementDates;

    if (companyHasMeasurementDate) {
      const measurementDateSlug = getMeasurementDateSlug(menuItem);

      services = services.map(item => ({
        ...item,
        measurementDateSlug,
      }));
    }

    return services;
  };

  const hasFundPermission = useMemo(() => {
    if (user?.is_superuser) {
      return true;
    }

    if (fundList && !isEmpty(fundList)) {
      return true;
    }

    if (otherPermissions && !isEmpty(otherPermissions)) {
      return otherPermissions.some(({ feature_object }: any) => feature_object.object_type === FUND_PERMISSION_NAME);
    }

    return false;
  }, [user, fundList, otherPermissions]);

  const fundsMenuItems = useMemo(() => {
    if (!fundList && !fundInfo) {
      return null;
    }

    if (fundInfo && isRestrictedUser && firmInfo) {
      return [
        {
          id: fundInfo.id,
          title: fundInfo.name,
          slug: fundInfo.slug,
          url: fundSummaryUrl(firmInfo.slug, fundInfo.slug),
        },
      ];
    }

    if (isEmpty(fundList)) {
      return [];
    }

    const tmpFundList = fundList.map((item: any) => {
      const isPinned = getArrayValue(pinnedFunds).some((fund: any) => fund.value === item.id);

      return {
        id: item.id,
        title: item.name,
        slug: item.slug,
        url: fundSummaryUrl(firmInfo.slug, item.slug),
        isPinnable: true,
        isPinned,
        updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned, 'fund'),
      };
    });

    return orderBy(tmpFundList, ['isPinned'], ['desc']);
  }, [fundList, fundInfo, isRestrictedUser, firmInfo, pinnedFunds, updatePinStatus]);

  const updateFundPins = useCallback(
    (options: any) => {
      setPinnedFunds(options);
    },
    [setPinnedFunds]
  );

  const fundsMenuItem = useMemo(() => {
    if (hasFundPermission) {
      const baseFundsMenuItem = {
        title: 'Funds',
        slug: 'funds',
        url: signInPath,
        icon: <Timeline />,
        subMenu: fundsMenuItems,
        filterComponent:
          fundsMenuItems?.length && fundsMenuItems.length > 1 ? (
            <FundFilter open={open} setOpenSidebar={setOpenSidebar!} updatePins={updateFundPins} />
          ) : null,
      };

      if (isRestrictedUser || viewOnlyUser) {
        return baseFundsMenuItem;
      }

      return {
        ...baseFundsMenuItem,
        actionMenu: {
          title: 'New Fund',
          slug: 'new-fund',
          url: '/#',
          icon: <Add />,
          callback: () => setDisplayFundDialogForm(true),
        },
      };
    }

    return null;
  }, [hasFundPermission, fundsMenuItems, open, setOpenSidebar, updateFundPins, viewOnlyUser, isRestrictedUser, setDisplayFundDialogForm]);

  const getCompanySubMenuItems = useCallback(
    ({ isFundInvestments = false }) => {
      if (!companyList) {
        return null;
      }

      if (isEmpty(companyList)) {
        return [];
      }

      const updateCompanyPinStatus = (id: number, title: string, isPinned: boolean) => {
        const type = isFundInvestments ? PINNABLE_TYPES.FUND_INVESTMENT : PINNABLE_TYPES.COMPANY;
        updatePinStatus(id, title, isPinned, type);
      };

      let tmpCompanyList: SubMenuItem[] = [];
      if (companyList) {
        const itemList = companyList.filter(({ company_type: companyType = '' }: any) =>
          isFundInvestments ? companyType === Company.type.INVESTMENT : companyType !== Company.type.INVESTMENT
        );

        tmpCompanyList = itemList.map((item: any) => {
          const isPinned = isFundInvestments
            ? getArrayValue(pinnedFundInvestments).some(
              (fundInvestment: any) => fundInvestment.value === item.company_id
            )
            : getArrayValue(pinnedCompanies).some((company: any) => company.value === item.company_id);

          return {
            id: item.company_id,
            isFundInvestments,
            isPinnable: true,
            isPinned,
            measurementDateSlug: getMeasurementDateSlug(item),
            slug: item.company_slug,
            subMenu: getCompanySubMenu(item),
            title: item.company_name,
            updatePinStatus: updateCompanyPinStatus,
            url: isFundInvestments
              ? getFundInvestmentsSummaryUrl(firmInfo.slug, item.company_slug)
              : companySummaryUrl(firmInfo.slug, item.company_slug),
          };
        });
      }

      return orderBy(tmpCompanyList, 'isPinned', 'desc');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyList, pinnedCompanies, pinnedFundInvestments]
  );

  const updatePins = useCallback(
    (options: any) => {
      setPinnedCompanies(options);
    },
    [setPinnedCompanies]
  );

  const companiesMenuItem = useMemo(() => {
    const companySubMenuItems = getCompanySubMenuItems({});

    const minMenuForRestrictedUser = {
      title: areFundInvestmentsEnabled ? FUND_INVESTMENTS_DIRECT_INVESTMENTS_TITLE : FUND_INVESTMENTS_COMPANIES_TITLE,
      slug: 'companies',
      url: '/icons',
      icon: areFundInvestmentsEnabled ? <MonetizationOnOutlined /> : <Store />,
      subMenu: companySubMenuItems,
      filterComponent:
        companySubMenuItems?.length && companySubMenuItems.length > 1 ? (
          <ItemsFilter open={open} setOpenSidebar={setOpenSidebar!} updatePins={updatePins} />
        ) : null,
    };

    if (!isRestrictedUser) {
      return {
        ...minMenuForRestrictedUser,
        actionMenu: viewOnlyUser
          ? null
          : {
            title: areFundInvestmentsEnabled
              ? FUND_INVESTMENTS_NEW_DIRECT_INVESTMENT_TITLE
              : FUND_INVESTMENTS_NEW_COMPANY_TITLE,
            slug: 'new-company',
            href: '/#',
            icon: <Add />,
            callback: handleClick,
          },
      };
    }

    if (isRestrictedUser && hasFundPermission) {
      return minMenuForRestrictedUser;
    }

    const {
      company_id: companyId,
      company_name: companyName,
      company_slug: companySlug,
      measurement_date_slug: measurementDateSlug,
    } = companyInfo;

    const companySubMenuItem = [
      {
        id: companyId,
        title: companyName,
        slug: companySlug,
        subMenu: companyPages,
        url: firmInfo && companySummaryUrl(firmInfo.slug, companySlug),
        isPinnable: false,
        measurementDateSlug:
          companyList
            ?.find((company: any) => company.id === companyId)
            ?.company_measurement_dates?.find((cmd: any) => cmd.date === commonMeasurementDate)?.slug ??
          measurementDateSlug,
      },
    ];

    return {
      title: areFundInvestmentsEnabled ? FUND_INVESTMENTS_DIRECT_INVESTMENTS_TITLE : FUND_INVESTMENTS_COMPANIES_TITLE,
      slug: 'companies',
      url: '/icons',
      icon: areFundInvestmentsEnabled ? <MonetizationOnOutlined /> : <Store />,
      subMenu: companySubMenuItem,
    };
  }, [
    getCompanySubMenuItems,
    areFundInvestmentsEnabled,
    open,
    setOpenSidebar,
    updatePins,
    isRestrictedUser,
    hasFundPermission,
    companyInfo,
    companyPages,
    firmInfo,
    companyList,
    viewOnlyUser,
    handleClick,
    commonMeasurementDate,
  ]);

  const updateFundInvestmentsPins = useCallback(
    (options: any) => {
      setPinnedFundInvestments(options);
    },
    [setPinnedFundInvestments]
  );

  const fundInvestmentsMenuItem = useMemo(() => {
    const fundInvestmentSubMenuItems = getCompanySubMenuItems({
      isFundInvestments: true,
    });

    const minMenuForRestrictedUser = {
      title: FUND_INVESTMENTS_TITLE,
      slug: FUND_INVESTMENTS_SLUG,
      url: '/icons',
      icon: <AccountBalanceOutlined />,
      subMenu: fundInvestmentSubMenuItems,
      filterComponent:
        fundInvestmentSubMenuItems?.length && fundInvestmentSubMenuItems.length > 1 ? (
          <ItemsFilter
            isFundInvestments
            open={open}
            setOpenSidebar={setOpenSidebar!}
            updatePins={updateFundInvestmentsPins}
          />
        ) : null,
    };

    if (!isRestrictedUser) {
      return {
        ...minMenuForRestrictedUser,
        actionMenu: viewOnlyUser
          ? null
          : {
            title: FUND_INVESTMENTS_NEW_FUND_INVESTMENT_TITLE,
            slug: FUND_INVESTMENTS_NEW_FUND_INVESTMENT_SLUG,
            href: '/#',
            icon: <Add />,
            callback: () => setDisplayFundInvestmentDialogForm(true),
          },
      };
    }

    if (isRestrictedUser && hasFundPermission) {
      return minMenuForRestrictedUser;
    }

    const {
      company_id: fundInvestmentId,
      company_name: fundInvestmentName,
      company_slug: fundInvestmentSlug,
      measurement_date_slug: measurementDateSlug,
    } = companyInfo;

    const companySubMenuItem = [
      {
        id: fundInvestmentId,
        title: fundInvestmentName,
        slug: fundInvestmentSlug,
        subMenu: fundInvestmentsPages,
        url: firmInfo && companySummaryUrl(firmInfo.slug, fundInvestmentSlug),
        isPinnable: false,
        measurementDateSlug:
          companyList
            ?.find((fundInvestment: any) => fundInvestment.id === fundInvestmentId)
            ?.company_measurement_dates?.find((cmd: any) => cmd.date === commonMeasurementDate)?.slug ??
          measurementDateSlug,
      },
    ];

    return {
      title: FUND_INVESTMENTS_TITLE,
      slug: FUND_INVESTMENTS_SLUG,
      url: '/icons',
      icon: <AccountBalanceOutlined />,
      subMenu: companySubMenuItem,
    };
  }, [
    getCompanySubMenuItems,
    open,
    setOpenSidebar,
    updateFundInvestmentsPins,
    isRestrictedUser,
    hasFundPermission,
    companyInfo,
    fundInvestmentsPages,
    firmInfo,
    companyList,
    viewOnlyUser,
    setDisplayFundInvestmentDialogForm,
    commonMeasurementDate,
  ]);

  const menuItems = useMemo(() => {
    let menu: MenuItem[] = [];

    if (fundsMenuItem) {
      menu = [...menu, fundsMenuItem as MenuItem];
    }

    menu = [...menu, companiesMenuItem as MenuItem];

    if (areFundInvestmentsEnabled) {
      menu = [...menu, fundInvestmentsMenuItem as MenuItem];
    }
    return menu;
  }, [fundsMenuItem, companiesMenuItem, fundInvestmentsMenuItem, areFundInvestmentsEnabled]);

  // Use the keyboard shortcuts hook
  useKeyboardShortcuts({
    setOpenSidebar,
  });

  const closeDropDown = useCallback(() => {
    setSubMenuItem(null);
    setAnchorEl(null);
    setAnchorElement(null);
  }, [setSubMenuItem, setAnchorEl]);

  const handleDropDown = useCallback(
    (option: Option, subMenuItemParam: SubMenuItem) => {
      setOpenSidebar?.(false);

      if (!firmInfo?.slug || !subMenuItemParam.slug) {
        return;
      }

      const {
        id: subMenuItemId,
        slug: subMenuItemSlug,
        measurementDateSlug: subMenuItemMeasurementDateSlug,
        isFundInvestments: subMenuItemIsFundInvestments,
      } = getObjectValue(subMenuItemParam);

      if (!subMenuItemSlug) {
        return;
      }

      // Do not execute if there are pending changes and the user wants to stay in the page
      if (!action && companyInfo.id !== subMenuItemId) {
        dispatch(captableAction.setCaptableInfo({}));
        dispatch(allocationsAction.setAllocationInfo({}));
      }

      const pathname = subMenuItemIsFundInvestments
        ? `${getBaseFundInvestmentsUrl(firmInfo.slug, subMenuItemSlug)}/${option.slug}`
        : `${baseCompanyUrl(firmInfo.slug, subMenuItemSlug)}/${option.slug}`;
      const search = queryString.stringify({
        date: subMenuItemMeasurementDateSlug,
        ...(option?.tab ? { tab: option.tab } : {}),
      });

      navigate({ pathname, search });
    },
    [action, companyInfo?.id, firmInfo?.slug, dispatch, navigate, setOpenSidebar]
  );

  useEffect(() => {
    if (!anchorEl || !anchorElement || !dropDownRef.current) {
      return;
    }

    // getBoundingClientRect() gives the element’s position in viewport pixels.
    // This is required because the dropdown is rendered in a Portal and uses
    // position: fixed, so parent offsets or zoom do not apply.
    const anchorRect = anchorElement.getBoundingClientRect();
    const { top, right } = anchorRect;

    const dropdown = dropDownRef.current;

    // Apply layout + animation-ready styles
    dropdown.style.left = `${right}px`;
    dropdown.style.opacity = '1';
    dropdown.style.top = `${top}px`;
    dropdown.style.transform = 'translateX(0px)';
    dropdown.style.visibility = 'visible';
  }, [anchorEl, anchorElement]);

  return (
    <Root onMouseLeave={closeDropDown}>
      <DropDown open={open} handleDropDown={handleDropDown} dropDownRef={dropDownRef} subMenuItem={subMenuItem} />
      <NativeScroll options={{ suppressScrollX: true, suppressScrollY: false }}>
        <TopContainer>
          {renderFundsAndCompanies && (
            <ListItems
              setOpenSidebar={setOpenSidebar ?? (() => {})}
              open={open}
              menuItems={menuItems}
              setSubMenuItem={setSubMenuItem}
              setAnchorEl={(rect, element) => {
                setAnchorEl(rect);
                setAnchorElement(element);
              }}
              closeDropDown={closeDropDown}
            />
          )}
        </TopContainer>
      </NativeScroll>
    </Root>
  );
};

export default TopMenuItems;
